import React from 'react';
import { ArrowLeft, ArrowRight, Check } from 'react-bootstrap-icons';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Typography } from 'antd';

import { useAppDispatch } from '@/hooks/redux';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { blockedStatus, enStatus, IModule } from '@/interfaces/course';
import { ILesson } from '@/interfaces/lesson';
import { coursesApi } from '@/services/course';
import { useManualWatchedLessonMutation } from '@/services/lesson';
import styled, { StyledProp, cx } from '@/styled';

import Stars from './Stars';

interface IControls {
  isLessonWatched?: boolean;
  nextLesson: ILesson['next'] | undefined;
  previousLesson: ILesson['previous'] | undefined;
  status: enStatus;
}

function Controls({ className, isLessonWatched, nextLesson, previousLesson, status }: StyledProp & IControls) {
  const breakpoints = useBreakpoints();
  const navigate = useNavigate();
  const { hash, id } = useParams();
  const dispatch = useAppDispatch();

  const [watched, { isLoading }] = useManualWatchedLessonMutation();

  const watchedLesson = () => {
    watched({ lessonId: id, watched: !isLessonWatched });
    updateProgress();
    updateModules();
  };

  const remapWatchedLessons = (draft: IModule[]) => {
    return (draft ?? []).map(({ lessons, ...modules }) => ({
      ...modules,
      lessons: lessons.map(lesson => ({
        ...lesson,
        watched: String(lesson.id) === id || lesson.watched
      }))
    }));
  };

  const updateModules = () => {
    dispatch(
      coursesApi.util.updateQueryData('getModules', { courseHash: hash }, draft => {
        Object.assign(draft, {
          ...draft,
          data: remapWatchedLessons(draft?.data)
        });
      })
    );
  };

  const updateProgress = () => {
    dispatch(
      coursesApi.util.updateQueryData('getProgress', hash, draft => {
        if (draft) Object.assign(draft, remapWatchedLessons(draft));
      })
    );
  };

  const handleGoToNextLesson = () => {
    if (nextLesson) navigate(`/curso/${hash}/aula/${nextLesson.lesson}`);
  };

  const handleGoToPreviousLesson = () => {
    if (previousLesson) navigate(`/curso/${hash}/aula/${previousLesson.lesson}`);
  };

  return (
    <div className={className}>
      <div className={cx(!previousLesson && 'hide', 'previousLesson')}>
        <Button onClick={handleGoToPreviousLesson} disabled={blockedStatus.includes(status) || !previousLesson}>
          <ArrowLeft size={18} />
        </Button>
        {breakpoints.up('md') && (
          <div>
            <Typography.Text className='controlName'>Aula Anterior</Typography.Text>
            <Typography.Text className='title'>{previousLesson?.title}</Typography.Text>
          </div>
        )}
      </div>

      {!blockedStatus.includes(status) && (
        <div className='centerButtons'>
          <Button
            disabled={isLessonWatched}
            onClick={watchedLesson}
            loading={isLoading}
            icon={isLessonWatched && <Check size={20} />}
            className={isLessonWatched ? 'watchLesson watched' : 'watchLesson'}
            id={`${isLessonWatched ? 'watched-lesson' : 'mark-as-concluded'}`}
          >
            {isLessonWatched ? 'Aula Concluída' : 'Marcar como Concluída'}
          </Button>
          <Stars />
        </div>
      )}
      <div className={cx(!nextLesson && 'hide', 'nextLesson')}>
        {breakpoints.up('md') && (
          <div>
            <Typography.Text className='controlName'>Próxima Aula</Typography.Text>
            <Typography.Text className='title'>{nextLesson?.title}</Typography.Text>
          </div>
        )}

        <Button disabled={!nextLesson || blockedStatus.includes(status)} onClick={handleGoToNextLesson}>
          <ArrowRight size={18} />
        </Button>
      </div>
    </div>
  );
}

export default styled(Controls)`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.space.spacing(9)};

  .hide {
    visibility: hidden;
  }

  > div {
    display: flex;

    span {
      color: ${({ theme }) => theme.colorTextSecondary};
      display: block;

      &.controlName {
        letter-spacing: 2px;
        color: ${({ theme }) => theme.colorTextTertiary};
        text-transform: uppercase;
        margin-top: ${({ theme }) => theme.space.pxToRem(8)};
      }

      &.title {
        max-width: 200px;
      }
    }

    button {
      background: ${({ theme }) => theme.variables.bgComponentsContainer};
      border: 0;
      height: 80px;

      &:disabled {
        background: ${({ theme }) => theme.variables.bgElevated};
      }
    }

    &.nextLesson,
    &.previousLesson {
      gap: ${({ theme }) => theme.space.spacing(4)};
    }
  }

  .nextLesson {
    span {
      text-align: right;
    }
  }

  .centerButtons {
    display: flex;
    flex-direction: column;
    align-items: center;

    .watchLesson {
      background: ${({ theme }) => theme.variables.contentButtonWatchLesson.background};
      font-weight: 500;
      display: flex;
      gap: 6px;
      align-items: center;
      justify-content: space-between;
      height: 40px;
      padding: ${({ theme }) => theme.space.spacing(5, 4, 5, 3)};

      span {
        color: ${({ theme }) => theme.variables.contentButtonWatchLesson.color};
      }

      &.watched {
        background: ${({ theme }) => theme.palette.gray[12]};
        border: 1px solid ${({ theme }) => theme.palette.gray[0]};

        span,
        svg {
          color: ${({ theme }) => theme.palette.gray[0]};
        }
      }
    }
  }

  ${({ theme }) => theme.breakpoints.down('lg')} {
    .nextLesson,
    .previousLesson {
      button {
        border-radius: 8px 0 0 8px;
      }
    }
  }
`;
